import React, { useEffect, useRef, useState } from "react"
import axios from "axios"
import Cropper from "react-cropper"
import "cropperjs/dist/cropper.css"
import { Crop, Image as ImageIcon, Loader2, Trash2, Upload, X } from "lucide-react"
import {
    Button,
    ImageFileId,
    Modal,
    Popconfirm,
    Progress,
    invalidateImageFileCache,
    message,
} from "../../ui/ot"

const MAX_FILES_PER_BATCH = 5
const MAX_CONCURRENT_UPLOADS = 2
const UPLOAD_TIMEOUT_MS = 30000
const ALLOWED_IMAGE_TYPES = new Set(["image/jpeg", "image/jpg", "image/png"])
const ALLOWED_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png"])

const createUploadId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID()
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const getFileExtension = (fileName = "") => fileName.split(".").pop()?.toLowerCase() || ""

const inferImageType = (fileName = "") => getFileExtension(fileName) === "png" ? "image/png" : "image/jpeg"

const validateImageFile = (file) => {
    if (!ALLOWED_IMAGE_TYPES.has(file.type) || !ALLOWED_IMAGE_EXTENSIONS.has(getFileExtension(file.name))) {
        return `${file.name}：僅支援 JPG/JPEG、PNG 格式`
    }
    return null
}

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error || new Error("圖片預覽讀取失敗"))
    reader.readAsDataURL(file)
})

const canvasToBlob = (canvas, type) => new Promise((resolve, reject) => {
    canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error("圖片裁切失敗")),
        type,
        type === "image/png" ? undefined : 0.92,
    )
})

const responseImages = (response) => response?.data?.values?.data || []

const getErrorMessage = (error, fallback) => (
    error?.response?.data?.values?.error
    || error?.response?.data?.message
    || (error?.code === "ECONNABORTED" ? "處理逾時，請稍後重試" : fallback)
)

const BookCoverManager = ({
    biblionumber,
    title,
    isbn,
    initialFileIds = [],
    onChanged = () => { },
    buttonText = "管理圖片",
    buttonSize = "small",
    buttonBlock = false,
    buttonVariant = "outline",
    className,
}) => {
    const [open, setOpen] = useState(false)
    const [images, setImages] = useState([])
    const [pendingItems, setPendingItems] = useState([])
    const [loading, setLoading] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [progress, setProgress] = useState(0)
    const [cropTarget, setCropTarget] = useState(null)
    const [cropping, setCropping] = useState(false)
    const cropperRef = useRef(null)

    const emitChanged = (nextImages) => {
        onChanged({
            biblionumber,
            images: nextImages,
            fileIds: nextImages.map((image) => Number(image.file_id)),
        })
    }

    const loadImages = async ({ notify = false } = {}) => {
        if (!biblionumber) return []
        setLoading(true)
        try {
            const response = await axios.get(`/koha/cover_images/biblio/${biblionumber}`)
            const nextImages = responseImages(response)
            setImages(nextImages)
            if (notify) emitChanged(nextImages)
            return nextImages
        } catch (error) {
            const fallbackImages = (initialFileIds || []).map((fileId) => ({ file_id: fileId }))
            setImages(fallbackImages)
            message.error(getErrorMessage(error, "書目圖片載入失敗"))
            return fallbackImages
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!open) return
        setPendingItems([])
        setCropTarget(null)
        setProgress(0)
        loadImages()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, biblionumber])

    const closeManager = () => {
        if (pendingItems.length > 0 && !window.confirm("尚有未上傳的圖片，確定要關閉嗎？")) return
        setOpen(false)
    }

    const prepareSelectedFiles = async (selectedFiles) => {
        const files = Array.from(selectedFiles || [])
        if (files.length === 0) return
        const errors = files.map(validateImageFile).filter(Boolean)
        const validFiles = files.filter((file) => !validateImageFile(file))

        if (errors.length > 0) message.error(errors.join("\n"))
        if (validFiles.length === 0) return
        if (pendingItems.length + validFiles.length > MAX_FILES_PER_BATCH) {
            message.error(`一次最多準備 ${MAX_FILES_PER_BATCH} 張圖片`)
            return
        }

        try {
            const nextItems = await Promise.all(validFiles.map(async (file) => ({
                id: createUploadId(),
                file,
                preview: await readFileAsDataUrl(file),
                status: "pending",
                error: null,
                cropped: false,
            })))
            setPendingItems((previous) => [...previous, ...nextItems])
        } catch (error) {
            message.error("圖片預覽讀取失敗，請重新選擇檔案")
        }
    }

    const uploadImage = async (file, replaceFileId = null) => {
        const formData = new FormData()
        formData.append("inputFile", file)
        const url = replaceFileId
            ? `/koha/cover_images/biblio/${biblionumber}/file/${replaceFileId}/replace`
            : `/koha/cover_images/biblio/${biblionumber}/file`
        return axios.post(url, formData, { timeout: UPLOAD_TIMEOUT_MS })
    }

    const handleUpload = async () => {
        const uploadItems = pendingItems.filter((item) => item.status === "pending" || item.status === "error")
        if (uploadItems.length === 0) return
        setUploading(true)
        setProgress(0)
        let completed = 0
        let failedCount = 0
        const queue = [...uploadItems]

        const worker = async () => {
            while (queue.length > 0) {
                const item = queue.shift()
                setPendingItems((previous) => previous.map((value) => value.id === item.id
                    ? { ...value, status: "uploading", error: null }
                    : value))
                try {
                    await uploadImage(item.file)
                    setPendingItems((previous) => previous.map((value) => value.id === item.id
                        ? { ...value, status: "success" }
                        : value))
                } catch (error) {
                    failedCount += 1
                    setPendingItems((previous) => previous.map((value) => value.id === item.id
                        ? { ...value, status: "error", error: getErrorMessage(error, "圖片上傳失敗") }
                        : value))
                } finally {
                    completed += 1
                    setProgress(Math.floor((completed / uploadItems.length) * 100))
                }
            }
        }

        await Promise.all(Array.from(
            { length: Math.min(MAX_CONCURRENT_UPLOADS, uploadItems.length) },
            () => worker(),
        ))
        const nextImages = await loadImages({ notify: true })
        setPendingItems((previous) => previous.filter((item) => item.status !== "success"))
        setUploading(false)

        if (nextImages.length > 0 && failedCount === 0) message.success("書目圖片上傳完成")
    }

    const startPendingCrop = (item) => {
        setCropTarget({
            kind: "pending",
            pendingId: item.id,
            file: item.file,
            preview: item.preview,
        })
    }

    const startExistingCrop = async (image) => {
        setCropping(true)
        try {
            const response = await axios.get("/component/file", {
                params: { file_id: image.file_id },
                responseType: "blob",
            })
            const fileName = image.file_client_name || `cover-${image.file_id}.jpg`
            const file = new File([response.data], fileName, {
                type: inferImageType(fileName),
                lastModified: Date.now(),
            })
            setCropTarget({
                kind: "existing",
                replaceFileId: image.file_id,
                file,
                preview: await readFileAsDataUrl(file),
            })
        } catch (error) {
            message.error(getErrorMessage(error, "原圖片讀取失敗"))
        } finally {
            setCropping(false)
        }
    }

    const applyCrop = async () => {
        const cropper = cropperRef.current?.cropper
        if (!cropper || !cropTarget) return
        setCropping(true)
        try {
            const canvas = cropper.getCroppedCanvas({
                maxWidth: 2400,
                maxHeight: 3200,
                imageSmoothingEnabled: true,
                imageSmoothingQuality: "high",
            })
            if (!canvas) throw new Error("圖片裁切失敗")

            const outputType = cropTarget.file.type === "image/png" ? "image/png" : "image/jpeg"
            const blob = await canvasToBlob(canvas, outputType)
            const croppedFile = new File([blob], cropTarget.file.name, {
                type: outputType,
                lastModified: Date.now(),
            })
            const preview = await readFileAsDataUrl(croppedFile)

            if (cropTarget.kind === "pending") {
                setPendingItems((previous) => previous.map((item) => item.id === cropTarget.pendingId
                    ? { ...item, file: croppedFile, preview, cropped: true, status: "pending", error: null }
                    : item))
                setCropTarget(null)
                message.success("圖片裁切完成，尚未上傳")
                return
            }

            const response = await uploadImage(croppedFile, cropTarget.replaceFileId)
            // replace 會沿用原本的 file_id，不清掉快取畫面會繼續顯示裁切前的圖
            invalidateImageFileCache(cropTarget.replaceFileId)
            const nextImages = responseImages(response)
            setImages(nextImages)
            emitChanged(nextImages)
            setCropTarget(null)
            message.success("圖片裁切更新完成")
        } catch (error) {
            message.error(getErrorMessage(error, error?.message || "圖片裁切失敗"))
        } finally {
            setCropping(false)
        }
    }

    const deleteImage = async (fileId) => {
        setLoading(true)
        try {
            const response = await axios.delete(`/koha/cover_images/biblio/${biblionumber}/file/${fileId}`)
            invalidateImageFileCache(fileId)
            const nextImages = responseImages(response)
            setImages(nextImages)
            emitChanged(nextImages)
            message.success("書目圖片已刪除")
        } catch (error) {
            message.error(getErrorMessage(error, "書目圖片刪除失敗"))
        } finally {
            setLoading(false)
        }
    }

    const handleButtonClick = (event) => {
        event?.preventDefault?.()
        event?.stopPropagation?.()
        if (!biblionumber) {
            message.error("缺少書目編號，無法管理圖片")
            return
        }
        setOpen(true)
    }

    return (
        <>
            <Button
                size={buttonSize}
                block={buttonBlock}
                variant={buttonVariant}
                className={className}
                onClick={handleButtonClick}
                disabled={!biblionumber}
            >
                <ImageIcon className="mr-1 h-4 w-4" />
                {buttonText}
            </Button>
            <Modal
                open={open}
                title={cropTarget ? "裁切書目圖片" : "書目圖片管理"}
                width={980}
                footer={null}
                cancelText="關閉"
                maskClosable={false}
                destroyOnClose
                onCancel={closeManager}
            >
                <div className="space-y-4">
                    <div className="rounded-md bg-muted p-3 text-sm">
                        <div className="font-medium">{title || "未命名書目"}</div>
                        <div className="text-muted-foreground">
                            書目編號：{biblionumber}{isbn ? ` ・ ISBN：${isbn}` : ""}
                        </div>
                    </div>

                    {cropTarget ? (
                        <div className="space-y-4">
                            <div className="max-h-[60vh] overflow-hidden rounded-md bg-black">
                                <Cropper
                                    ref={cropperRef}
                                    src={cropTarget.preview}
                                    style={{ height: 520, width: "100%" }}
                                    viewMode={1}
                                    guides
                                    responsive
                                    background={false}
                                    autoCropArea={0.9}
                                    checkOrientation={false}
                                />
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={() => setCropTarget(null)} disabled={cropping}>
                                    取消
                                </Button>
                                <Button onClick={applyCrop} disabled={cropping}>
                                    {cropping && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    {cropTarget.kind === "existing" ? "套用並取代原圖片" : "套用裁切"}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <section className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-base font-medium">目前圖片（{images.length}）</h3>
                                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                                </div>
                                {images.length === 0 && !loading ? (
                                    <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                                        此書目尚未綁定圖片
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                                        {images.map((image) => (
                                            <div key={image.file_id} className="overflow-hidden rounded-md border bg-card">
                                                <div className="aspect-[3/4] bg-muted">
                                                    <ImageFileId
                                                        file_id={image.file_id}
                                                        alt={`${title || "書目"}圖片`}
                                                        customize
                                                        className="h-full w-full object-contain"
                                                    />
                                                </div>
                                                <div className="flex gap-2 p-2">
                                                    <Button
                                                        size="small"
                                                        variant="outline"
                                                        className="flex-1"
                                                        onClick={() => startExistingCrop(image)}
                                                        disabled={loading || cropping || uploading}
                                                    >
                                                        <Crop className="mr-1 h-4 w-4" />裁切
                                                    </Button>
                                                    <Popconfirm
                                                        title="刪除書目圖片"
                                                        description="只會解除這張圖片與目前書目的關聯，確定刪除？"
                                                        okText="刪除"
                                                        cancelText="取消"
                                                        onConfirm={() => deleteImage(image.file_id)}
                                                    >
                                                        <Button
                                                            size="small"
                                                            variant="destructive"
                                                            disabled={loading || cropping || uploading}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </Popconfirm>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>

                            <section className="space-y-3 border-t pt-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                        <h3 className="text-base font-medium">新增圖片</h3>
                                        <p className="text-xs text-muted-foreground">JPG/JPEG、PNG；一次最多 {MAX_FILES_PER_BATCH} 張</p>
                                    </div>
                                    <label className="cursor-pointer">
                                        <span className="inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium">
                                            <Upload className="mr-2 h-4 w-4" />選擇圖片
                                        </span>
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg,image/jpg"
                                            multiple
                                            className="sr-only"
                                            disabled={uploading || pendingItems.length >= MAX_FILES_PER_BATCH}
                                            onChange={(event) => {
                                                prepareSelectedFiles(event.target.files)
                                                event.target.value = ""
                                            }}
                                        />
                                    </label>
                                </div>

                                <div
                                    className="rounded-md border border-dashed p-4"
                                    onDragOver={(event) => event.preventDefault()}
                                    onDrop={(event) => {
                                        event.preventDefault()
                                        if (!uploading) prepareSelectedFiles(event.dataTransfer.files)
                                    }}
                                >
                                    {pendingItems.length === 0 ? (
                                        <div className="py-5 text-center text-sm text-muted-foreground">拖放圖片到這裡，或點選上方「選擇圖片」</div>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                                            {pendingItems.map((item) => (
                                                <div key={item.id} className="relative overflow-hidden rounded-md border">
                                                    <img src={item.preview} alt={item.file.name} className="aspect-[3/4] w-full object-cover" />
                                                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-black/60 p-2">
                                                        <Button
                                                            size="small"
                                                            variant="secondary"
                                                            onClick={() => startPendingCrop(item)}
                                                            disabled={uploading}
                                                        >
                                                            <Crop className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="destructive"
                                                            onClick={() => setPendingItems((previous) => previous.filter((value) => value.id !== item.id))}
                                                            disabled={uploading}
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                    {item.status === "uploading" && (
                                                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white">
                                                            <Loader2 className="h-6 w-6 animate-spin" />
                                                        </div>
                                                    )}
                                                    {item.error && (
                                                        <div className="absolute inset-x-0 top-0 bg-red-600/90 p-1 text-xs text-white">{item.error}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {uploading && <Progress value={progress} className="h-2" />}
                                <div className="flex justify-end gap-2">
                                    <Button
                                        variant="outline"
                                        onClick={() => setPendingItems([])}
                                        disabled={uploading || pendingItems.length === 0}
                                    >
                                        清除待上傳圖片
                                    </Button>
                                    <Button onClick={handleUpload} disabled={uploading || pendingItems.length === 0}>
                                        {uploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        上傳並綁定 {pendingItems.length} 張圖片
                                    </Button>
                                </div>
                            </section>
                        </>
                    )}
                </div>
            </Modal>
        </>
    )
}

export default BookCoverManager
